import { useState, useEffect } from 'react'
import { useSQLDatabaseCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { FieldTooltip } from '../shared/FieldTooltip'
import { DiscountModifier } from '../shared/DiscountModifier'
import { MobileCostBar } from '../shared/MobileCostBar'
import { ScenarioPresets } from '../shared/ScenarioPresets'
import { Toggle } from '../ui/Toggle'
import { Select } from '../ui/Select'
import type { SQLDatabaseRequest } from '../../api/types'
import { useCalculatorStore } from '../../store/calculatorStore'

// DTU SKU definitions
const DTU_SKUS = {
  basic:    [{ value: 'B',   label: 'B — 5 DTU' }],
  standard: [
    { value: 'S0',  label: 'S0 — 10 DTU' },
    { value: 'S1',  label: 'S1 — 20 DTU' },
    { value: 'S2',  label: 'S2 — 50 DTU' },
    { value: 'S3',  label: 'S3 — 100 DTU' },
    { value: 'S4',  label: 'S4 — 200 DTU' },
    { value: 'S6',  label: 'S6 — 400 DTU' },
    { value: 'S7',  label: 'S7 — 800 DTU' },
    { value: 'S9',  label: 'S9 — 1,600 DTU' },
    { value: 'S12', label: 'S12 — 3,000 DTU' },
  ],
  premium: [
    { value: 'P1',  label: 'P1 — 125 DTU' },
    { value: 'P11', label: 'P11 — 1,750 DTU' },
    { value: 'P15', label: 'P15 — 4,000 DTU' },
  ],
}

const DEFAULT_DTU_SKU: Record<string, string> = {
  basic: 'B', standard: 'S0', premium: 'P1',
}

const defaultInputs: SQLDatabaseRequest = {
  purchaseModel: 'vcore',
  dtu: { tier: 'standard', sku: 'S3', storageGB: 100 },
  vcore: {
    serviceTier: 'general_purpose',
    computeTier: 'provisioned',
    vCores: 4,
    storageGB: 100,
    azureHybridBenefit: false,
    reservedInstance: 'none',
    savingsPlan: 'none',
    serverlessUtilisationPercent: 50,
  },
  elasticPool: { enabled: false, databaseCount: 5 },
  backupRedundancy: 'grs',
  backupStorageGB: 0,
}

function fmt(n: number) { return `£${n.toFixed(2)}` }
function fmtSaving(n: number) { return n > 0 ? `−${fmt(n)}` : '—' }

export default function SQLDatabaseCalc() {
  const [inputs, setInputs] = useState<SQLDatabaseRequest>(defaultInputs)
  const { discount, setDiscount } = useCalculatorStore()
  const { data, isLoading } = useSQLDatabaseCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.totalMonthly) {
      const tier = inputs.purchaseModel === 'dtu' ? inputs.dtu?.tier : inputs.vcore?.serviceTier
      trackCalculatorUsed({ calculator: 'sql-database', result_gbp: data.totalMonthly, tier })
    }
  }, [data?.totalMonthly])


  const totalBeforeDiscount = data?.totalMonthly ?? 0
  const visibleTotal = totalBeforeDiscount * (1 - discount / 100)

  function setPurchaseModel(model: 'dtu' | 'vcore') {
    setInputs((prev) => ({ ...prev, purchaseModel: model }))
  }

  function patchDtu(patch: Partial<NonNullable<SQLDatabaseRequest['dtu']>>) {
    setInputs((prev) => ({ ...prev, dtu: { ...prev.dtu!, ...patch } }))
  }

  function patchVcore(patch: Partial<NonNullable<SQLDatabaseRequest['vcore']>>) {
    setInputs((prev) => ({ ...prev, vcore: { ...prev.vcore!, ...patch } }))
  }

  function patchElasticPool(patch: Partial<SQLDatabaseRequest['elasticPool']>) {
    setInputs((prev) => ({ ...prev, elasticPool: { ...prev.elasticPool, ...patch } }))
  }

  const dtu = inputs.dtu!
  const vcore = inputs.vcore!
  const bd = data?.breakdown

  const perDbMonthly = inputs.elasticPool.enabled && inputs.elasticPool.databaseCount > 1
    ? visibleTotal / inputs.elasticPool.databaseCount
    : null

  // Determine formula text for disclosure
  const formulaLines: string[] = []
  if (data) {
    if (inputs.purchaseModel === 'dtu') {
      formulaLines.push(`Compute (${dtu.tier.toUpperCase()} ${dtu.sku}): ${fmt(bd!.compute)} (daily rate × 30 days)`)
      if (bd!.storage > 0) formulaLines.push(`Extra storage: ${fmt(bd!.storage)}`)
    } else {
      formulaLines.push(`Compute (${vcore.serviceTier.replace('_', ' ')} ${vcore.vCores} vCores ${vcore.computeTier}): ${fmt(bd!.compute)}`)
      formulaLines.push(`Storage (${vcore.storageGB} GB × £${vcore.serviceTier === 'general_purpose' ? '0.0995' : vcore.serviceTier === 'business_critical' ? '0.2163' : '0.2205'}/GB): ${fmt(bd!.storage)}`)
    }
    if (bd!.backup > 0) formulaLines.push(`Backup (${inputs.backupRedundancy.toUpperCase()}): ${fmt(bd!.backup)}`)
    if (bd!.hybridBenefitSaving > 0) formulaLines.push(`Azure Hybrid Benefit saving: −${fmt(bd!.hybridBenefitSaving)}`)
    if (bd!.reservedInstanceSaving > 0) formulaLines.push(`Reserved Instance saving: −${fmt(bd!.reservedInstanceSaving)}`)
    if (bd!.savingsPlanSaving > 0) formulaLines.push(`Savings Plan saving: −${fmt(bd!.savingsPlanSaving)}`)
  }

  return (
    <div className={`grid grid-cols-1 md:grid-cols-[1fr_380px] gap-8 w-full pb-14 md:pb-0 transition-opacity ${isLoading ? 'opacity-70' : ''}`}>

      {/* ── Left: inputs ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-6">

        <ScenarioPresets presets={[
          {
            label: 'Small app (DTU)',
            description: 'Standard S2 · 50 DTUs · 50 GB — typical small app database',
            apply: () => { setPurchaseModel('dtu'); patchDtu({ tier: 'standard', sku: 'S2', storageGB: 50 }); },
          },
          {
            label: 'Production (vCore)',
            description: 'General Purpose · 4 vCores · 100 GB — standard production setup',
            apply: () => { setPurchaseModel('vcore'); patchVcore({ serviceTier: 'general_purpose', vCores: 4, storageGB: 100, azureHybridBenefit: false }); },
          },
          {
            label: 'Production + Hybrid Benefit',
            description: 'General Purpose · 4 vCores · 100 GB + AHB — highest-impact cost reduction',
            apply: () => { setPurchaseModel('vcore'); patchVcore({ serviceTier: 'general_purpose', vCores: 4, storageGB: 100, azureHybridBenefit: true }); },
          },
        ]} />

        {/* Section 1: Purchase Model */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>database</span>
            Purchase Model
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            {(['dtu', 'vcore'] as const).map((model) => (
              <button
                key={model}
                onClick={() => setPurchaseModel(model)}
                className={`rounded-lg p-4 text-left border-2 transition-all ${
                  inputs.purchaseModel === model
                    ? 'border-[#0078d4] bg-[#0078d4]/15'
                    : 'border-transparent'
                }`}
                style={{
                  backgroundColor: inputs.purchaseModel === model ? undefined : 'var(--surface-container)',
                  borderColor: inputs.purchaseModel !== model ? 'var(--outline-variant)' : undefined,
                }}
              >
                <div className="font-mono text-[11px] font-bold uppercase tracking-widest text-[#0078d4] mb-1">
                  {model === 'dtu' ? 'DTU Model' : 'vCore Model'}
                </div>
                <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                  {model === 'dtu'
                    ? 'Fixed bundles — simpler but less flexible'
                    : 'Separate compute + storage — RI & AHB discounts'}
                </div>
              </button>
            ))}
          </div>

          <div className="bg-[#0078d4]/10 border border-[#0078d4]/30 rounded-sm p-3 flex gap-2 items-start">
            <span className="material-symbols-outlined text-[#0078d4] text-[16px] shrink-0 mt-0.5">info</span>
            <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
              <strong>Rough conversion:</strong> 100 DTUs ≈ 1 vCore (General Purpose). The DTU model bundles compute, storage, and IO — you can't apply Azure Hybrid Benefit or Reserved Instance discounts. If you have an EA or SQL Server licences, vCore is almost always cheaper.
            </p>
          </div>
        </div>

        {/* Section 2: DTU Configuration */}
        {inputs.purchaseModel === 'dtu' && (
          <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-5">
              <span className="material-symbols-outlined text-[#e8b000]" style={{ fontVariationSettings: "'FILL' 1" }}>tune</span>
              DTU Configuration
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Select
                label="Tier"
                value={dtu.tier}
                onChange={(v) => {
                  const tier = v as 'basic' | 'standard' | 'premium'
                  patchDtu({ tier, sku: DEFAULT_DTU_SKU[tier] })
                }}
                options={[
                  { value: 'basic',    label: 'Basic' },
                  { value: 'standard', label: 'Standard' },
                  { value: 'premium',  label: 'Premium' },
                ]}
              />

              <Select
                label="SKU"
                value={dtu.sku}
                onChange={(v) => patchDtu({ sku: v })}
                options={DTU_SKUS[dtu.tier]}
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Storage (GB)</label>
                <input
                  type="number"
                  min={0}
                  max={4096}
                  value={dtu.storageGB}
                  onChange={(e) => patchDtu({ storageGB: Number(e.target.value) })}
                  className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
                />
              </div>
            </div>

            <p className="text-[11px] italic" style={{ color: 'var(--text-tertiary)' }}>
              DTU tier includes storage: Basic 2 GB, Standard 250 GB, Premium up to 4 TB. Extra storage billed per GB above that.
            </p>
          </div>
        )}

        {/* Section 3: vCore Configuration */}
        {inputs.purchaseModel === 'vcore' && (
          <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
            <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-5">
              <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>memory</span>
              vCore Configuration
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Select
                label="Service Tier"
                value={vcore.serviceTier}
                onChange={(v) => patchVcore({ serviceTier: v as typeof vcore.serviceTier })}
                options={[
                  { value: 'general_purpose',   label: 'General Purpose' },
                  { value: 'business_critical',  label: 'Business Critical' },
                  { value: 'hyperscale',          label: 'Hyperscale' },
                ]}
              />

              <Select
                label="Compute Tier"
                value={vcore.computeTier}
                onChange={(v) => patchVcore({ computeTier: v as typeof vcore.computeTier })}
                options={
                  vcore.serviceTier === 'general_purpose'
                    ? [
                        { value: 'provisioned', label: 'Provisioned (always on)' },
                        { value: 'serverless',  label: 'Serverless (auto-pause)' },
                      ]
                    : [{ value: 'provisioned', label: 'Provisioned (always on)' }]
                }
              />

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>vCores <FieldTooltip content="Virtual cores assigned to your database. More vCores = higher CPU capacity and cost. Scales independently of storage in vCore model." /></label>
                <input
                  type="number"
                  min={2}
                  max={80}
                  step={2}
                  value={vcore.vCores}
                  onChange={(e) => patchVcore({ vCores: Math.max(2, Number(e.target.value)) })}
                  className="w-full rounded px-3 py-2.5 text-[13px] focus:outline-none focus:border-[#0078d4]"
                  style={{ backgroundColor: 'var(--surface-container)', border: '2px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Storage (GB)</label>
                <input
                  type="number"
                  min={5}
                  max={4096}
                  value={vcore.storageGB}
                  onChange={(e) => patchVcore({ storageGB: Number(e.target.value) })}
                  className="w-full rounded px-3 py-2.5 text-[13px] focus:outline-none focus:border-[#0078d4]"
                  style={{ backgroundColor: 'var(--surface-container)', border: '2px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>

            {/* Serverless utilisation */}
            {vcore.computeTier === 'serverless' && (
              <div className="mb-4 flex flex-col gap-1.5">
                <label className="text-[11px] font-mono text-outline uppercase tracking-wider">
                  Expected active utilisation: {vcore.serverlessUtilisationPercent}%
                </label>
                <input
                  type="range"
                  min={1}
                  max={100}
                  value={vcore.serverlessUtilisationPercent}
                  onChange={(e) => patchVcore({ serverlessUtilisationPercent: Number(e.target.value) })}
                  className="w-full accent-[#0078d4]"
                />
                <p className="text-[11px] italic" style={{ color: 'var(--text-tertiary)' }}>
                  Serverless bills per vCore-second while active. Pauses at £0 when idle. Use this slider to estimate what % of the month the DB will be actively queried.
                </p>
              </div>
            )}

            {/* Azure Hybrid Benefit */}
            <div className="mt-2 mb-4 pt-4" style={{ borderTop: '1px solid var(--outline-variant)' }}>
              <Toggle
                checked={vcore.azureHybridBenefit}
                onChange={(v) => patchVcore({ azureHybridBenefit: v })}
                label="Azure Hybrid Benefit"
                description="I have SQL Server licences with Software Assurance or SQL subscription (~30–50% compute saving)"
              />
            </div>

            {/* Commitment discounts */}
            <div className="pt-4" style={{ borderTop: '1px solid var(--outline-variant)' }}>
              <p className="text-[11px] font-mono text-outline uppercase tracking-wider mb-3">Commitment Discount</p>
              <div className="flex flex-col gap-2">
                {[
                  { value: 'none',   riLabel: 'No commitment (PAYG)', spLabel: null },
                  { value: '1year',  riLabel: 'Reserved Instance 1-year (~33% saving)', spLabel: 'Savings Plan 1-year (~35% saving)' },
                  { value: '3year',  riLabel: 'Reserved Instance 3-year (~55% saving)', spLabel: null },
                ].map(({ value, riLabel, spLabel }) => (
                  <div key={value} className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ri"
                        value={value}
                        checked={vcore.reservedInstance === value && vcore.savingsPlan === 'none'}
                        onChange={() => patchVcore({ reservedInstance: value as typeof vcore.reservedInstance, savingsPlan: 'none' })}
                        className="accent-[#0078d4]"
                      />
                      <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>{riLabel}</span>
                    </label>
                    {spLabel && (
                      <label className="flex items-center gap-2 cursor-pointer ml-1">
                        <input
                          type="radio"
                          name="ri"
                          checked={vcore.savingsPlan === '1year'}
                          onChange={() => patchVcore({ reservedInstance: 'none', savingsPlan: '1year' })}
                          className="accent-[#0078d4]"
                        />
                        <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>{spLabel}</span>
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {vcore.savingsPlan === '1year' && (
                <div className="mt-3 bg-[#107c10]/10 border border-[#107c10]/40 rounded-sm p-3 flex gap-2 items-start">
                  <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">lightbulb</span>
                  <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                    <strong className="text-[#107c10]">First-mover advantage:</strong> Microsoft announced the Savings Plan for Databases in March 2026. Their own pricing calculator has not yet been updated to model it. This estimate uses the published rates directly.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Section 4: Elastic Pool */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-[#0078d4]" style={{ fontVariationSettings: "'FILL' 1" }}>layers</span>
            Elastic Pool
          </h2>

          <Toggle
            checked={inputs.elasticPool.enabled}
            onChange={(v) => patchElasticPool({ enabled: v })}
            label="Elastic Pool enabled"
            description="Multiple databases sharing a resource pool — cost-effective when average utilisation is below ~50%"
          />

          {inputs.elasticPool.enabled && (
            <div className="mt-4 flex flex-col gap-1.5">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Number of databases in pool</label>
              <input
                type="number"
                min={2}
                max={500}
                value={inputs.elasticPool.databaseCount}
                onChange={(e) => patchElasticPool({ databaseCount: Math.max(2, Number(e.target.value)) })}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
              <p className="text-[11px] italic" style={{ color: 'var(--text-tertiary)' }}>
                The cost shown is the total pool cost. Per-database cost is shown in the results panel.
              </p>
            </div>
          )}
        </div>

        {/* Section 5: Backup */}
        <div className="bg-surface-container-high micro-gradient ghost-border rounded-lg p-6">
          <h2 className="font-headline font-bold text-lg flex items-center gap-2 mb-5">
            <span className="material-symbols-outlined text-[#107c10]" style={{ fontVariationSettings: "'FILL' 1" }}>backup</span>
            Backup Storage
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Backup Redundancy"
              value={inputs.backupRedundancy}
              onChange={(v) => setInputs((prev) => ({ ...prev, backupRedundancy: v as typeof prev.backupRedundancy }))}
              options={[
                { value: 'lrs',  label: 'LRS (locally redundant)' },
                { value: 'zrs',  label: 'ZRS (zone redundant)' },
                { value: 'grs',  label: 'GRS (geo redundant) — default' },
                { value: 'gzrs', label: 'GZRS (geo + zone redundant)' },
              ]}
            />

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-mono text-outline uppercase tracking-wider">Extra backup storage (GB)</label>
              <input
                type="number"
                min={0}
                value={inputs.backupStorageGB}
                onChange={(e) => setInputs((prev) => ({ ...prev, backupStorageGB: Number(e.target.value) }))}
                className="w-full rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--outline-variant)', color: 'var(--text-secondary)' }}
              />
            </div>
          </div>
          <p className="text-[11px] italic mt-2" style={{ color: 'var(--text-tertiary)' }}>
            LRS backup equal to 100% of database size is included free. GRS/ZRS/GZRS backups are charged from the first GB.
          </p>
        </div>
      </div>

      {/* ── Right: results ───────────────────────────────────────── */}
      <div className="flex flex-col gap-4">
        <div className="rounded-lg p-6 sticky top-[64px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Monthly estimate (PAYG)</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#0078d4]">UK South · GBP</span>
          </div>

          <div className="text-4xl font-bold font-mono text-[#0078d4] mb-1">
            {data ? fmt(visibleTotal) : '—'}
          </div>
          <div className="text-[11px] mb-4" style={{ color: 'var(--text-tertiary)' }}>
            {data ? `≈ £${data.effectiveHourlyRate}/hr effective` : 'Enter inputs to calculate'}
          </div>

          {perDbMonthly !== null && (
            <div className="mb-4 p-3 rounded-sm bg-[#0078d4]/10 border border-[#0078d4]/30">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#0078d4] mb-0.5">Per database</div>
              <div className="text-xl font-bold font-mono text-[#0078d4]">{fmt(perDbMonthly)}/month</div>
              <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                Pool of {inputs.elasticPool.databaseCount} databases
              </div>
            </div>
          )}

          {bd && (
            <div className="flex flex-col gap-1.5 mb-4 pt-4" style={{ borderTop: '1px solid var(--outline-variant)' }}>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>Compute</span>
                <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{fmt(bd.compute)}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span style={{ color: 'var(--text-secondary)' }}>Storage</span>
                <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{fmt(bd.storage)}</span>
              </div>
              {bd.backup > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span style={{ color: 'var(--text-secondary)' }}>Backup</span>
                  <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{fmt(bd.backup)}</span>
                </div>
              )}
              {bd.hybridBenefitSaving > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#107c10]">Azure Hybrid Benefit</span>
                  <span className="font-mono font-semibold text-[#107c10]">{fmtSaving(bd.hybridBenefitSaving)}</span>
                </div>
              )}
              {bd.reservedInstanceSaving > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#107c10]">Reserved Instance</span>
                  <span className="font-mono font-semibold text-[#107c10]">{fmtSaving(bd.reservedInstanceSaving)}</span>
                </div>
              )}
              {bd.savingsPlanSaving > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#107c10]">Savings Plan</span>
                  <span className="font-mono font-semibold text-[#107c10]">{fmtSaving(bd.savingsPlanSaving)}</span>
                </div>
              )}
              <div className="flex justify-between text-[14px] font-bold pt-2 mt-1" style={{ borderTop: '1px solid var(--outline-variant)' }}>
                <span style={{ color: 'var(--text-primary)' }}>Total</span>
                <span className="font-mono text-[#0078d4]">{fmt(visibleTotal)}</span>
              </div>
            </div>
          )}

          {/* DTU → vCore comparison */}
          {data?.dtuToVcoreComparison && (
            <div className="mb-4 p-3 rounded-sm bg-[#e8b000]/10 border border-[#e8b000]/40">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#e8b000] mb-1">DTU → vCore comparison</div>
              <div className="text-[12px] mb-1" style={{ color: 'var(--text-primary)' }}>
                Equivalent: ~{data.dtuToVcoreComparison.equivalentVcores} vCore General Purpose
              </div>
              <div className="flex justify-between text-[12px] mb-1">
                <span style={{ color: 'var(--text-tertiary)' }}>This DTU config</span>
                <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(data.dtuToVcoreComparison.dtusMonthlyCost)}/mo</span>
              </div>
              <div className="flex justify-between text-[12px] mb-2">
                <span style={{ color: 'var(--text-tertiary)' }}>vCore GP equivalent</span>
                <span className={`font-mono ${data.dtuToVcoreComparison.vcoreMonthlyCost < data.dtuToVcoreComparison.dtusMonthlyCost ? 'text-[#107c10]' : ''}`}>
                  {fmt(data.dtuToVcoreComparison.vcoreMonthlyCost)}/mo
                </span>
              </div>
              <p className="text-[11px] italic" style={{ color: 'var(--text-tertiary)' }}>
                {data.dtuToVcoreComparison.recommendation}
              </p>
            </div>
          )}

          {/* Savings Plan note */}
          {data?.savingsPlanNote && (
            <div className="mb-4 p-3 rounded-sm bg-[#107c10]/10 border border-[#107c10]/40 flex gap-2 items-start">
              <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0 mt-0.5">lightbulb</span>
              <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                {data.savingsPlanNote}
              </p>
            </div>
          )}

          <DiscountModifier
            discount={discount}
            onDiscountChange={setDiscount}
            paygTotal={totalBeforeDiscount}
          />

          {formulaLines.length > 0 && data?.prices_last_updated && (
            <FormulaDisclosure
              lines={formulaLines}
              fetchedAt={data.prices_last_updated}
            />
          )}
        </div>
      </div>

      <MobileCostBar amount={visibleTotal || null} label="SQL Database / month" targetId="sql-result" />
    </div>
  )
}
